import { useState } from 'react';
import { api } from '../lib/api';
import { KeyRound, Loader2, Send, ShieldCheck, Eye, EyeOff, Check } from 'lucide-react';

/**
 * AdminChangePinCard — Iter-51.57 (revised).
 *
 * Self-service rotation of the admin-only debug PIN, gated by:
 *   1) an explicit "Current PIN" retype (must match the stored PIN
 *      server-side — the drawer-unlock is NOT trusted as proof),
 *   2) a 6-digit OTP emailed to the FIXED admin address configured
 *      server-side via env `ADMIN_OTP_EMAIL` (never the currently-
 *      signed-in parent — so a compromised parent session can't
 *      rotate the PIN on its own).
 *
 * Two-step UI:
 *   1) Current PIN + New PIN + Confirm PIN → "Send OTP" → server
 *      re-verifies the Current PIN, then emails a 6-digit OTP to
 *      the fixed admin address. We show a masked hint.
 *   2) Enter OTP → "Verify & save" → new PIN is hashed + persisted.
 *      Current session stays live; the very next admin-panel open
 *      requires the NEW PIN.
 */
export default function AdminChangePinCard({ debugPin, onChanged }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [otp, setOtp] = useState('');
  const [reveal, setReveal] = useState(false);
  const [stage, setStage] = useState('idle'); // idle | otp-sent | done
  const [emailHint, setEmailHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');

  const validPinFormat = (p) => /^\d{4,8}$/.test(p);

  const sendOtp = async () => {
    setErr(''); setFlash('');
    if (!validPinFormat(currentPin)) return setErr('Enter your current PIN.');
    if (!validPinFormat(newPin)) return setErr('New PIN must be 4–8 digits.');
    if (newPin !== confirmPin) return setErr('New PINs do not match.');
    if (newPin === currentPin) return setErr('New PIN must be different from the current one.');
    setBusy(true);
    try {
      const { data } = await api.post('/admin/debug-pin/request-otp', { pin: currentPin });
      setEmailHint(data?.email_hint || 'the admin email');
      setStage('otp-sent');
      setFlash(`OTP sent to ${data?.email_hint || 'the admin email'}. Check the inbox.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not send OTP. Try again.');
    } finally { setBusy(false); }
  };

  const verifyAndSave = async () => {
    setErr(''); setFlash('');
    if (!/^\d{6}$/.test(otp)) return setErr('OTP must be 6 digits.');
    if (!validPinFormat(currentPin) || !validPinFormat(newPin)) {
      return setErr('Re-enter your PINs and try again.');
    }
    setBusy(true);
    try {
      await api.post('/admin/debug-pin/change', {
        pin: currentPin, otp, new_pin: newPin,
      });
      setStage('done');
      setFlash('PIN rotated. Use your new PIN next time you open the admin panel.');
      // Bubble the new PIN up so the parent screen can keep the current
      // admin drawer working with the fresh value (no forced re-unlock).
      onChanged && onChanged(newPin);
      // Clear sensitive fields.
      setCurrentPin(''); setNewPin(''); setConfirmPin(''); setOtp('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Verification failed.');
    } finally { setBusy(false); }
  };

  const inputsDisabled = stage === 'otp-sent' || busy;

  return (
    <div data-testid="admin-change-pin-card"
      className="mt-3 bg-white border border-slate-200 shadow-sm text-slate-800 rounded-2xl p-4 text-[13px]">
      <div className="flex items-center justify-between mb-3">
        <div className="text-slate-900 font-bold uppercase tracking-wider text-[13px] flex items-center gap-2">
          <KeyRound size={16}/> Change debug PIN
        </div>
        <button type="button" data-testid="admin-change-pin-reveal"
          onClick={() => setReveal(r => !r)}
          className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 hover:bg-slate-300 inline-flex items-center gap-1">
          {reveal ? <EyeOff size={12}/> : <Eye size={12}/>} {reveal ? 'Hide' : 'Show'}
        </button>
      </div>

      <p className="text-[13px] text-slate-600 mb-3 leading-relaxed">
        Rotate the admin-only PIN. Requires your current PIN plus a 6-digit
        code emailed to the fixed admin address. New PIN must be 4–8 digits
        and different from the current one.
      </p>

      <label className="block mb-2">
        <span className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1">Current PIN</span>
        <input data-testid="admin-change-pin-current"
          type={reveal ? 'text' : 'password'} inputMode="numeric"
          value={currentPin} maxLength={8}
          onChange={(e) => setCurrentPin(e.target.value.replace(/\D+/g, '').slice(0, 8))}
          disabled={inputsDisabled}
          autoComplete="off"
          className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 focus:border-amber-500 outline-none tracking-widest text-center disabled:opacity-50 text-slate-800"
          placeholder="••••••"/>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <label className="block">
          <span className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1">New PIN (4–8 digits)</span>
          <input data-testid="admin-change-pin-new"
            type={reveal ? 'text' : 'password'} inputMode="numeric"
            value={newPin} maxLength={8}
            onChange={(e) => setNewPin(e.target.value.replace(/\D+/g, '').slice(0, 8))}
            disabled={inputsDisabled}
            autoComplete="off"
            className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 focus:border-amber-500 outline-none tracking-widest text-center disabled:opacity-50 text-slate-800"
            placeholder="••••••"/>
        </label>
        <label className="block">
          <span className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1">Confirm new PIN</span>
          <input data-testid="admin-change-pin-confirm"
            type={reveal ? 'text' : 'password'} inputMode="numeric"
            value={confirmPin} maxLength={8}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D+/g, '').slice(0, 8))}
            disabled={inputsDisabled}
            autoComplete="off"
            className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 focus:border-amber-500 outline-none tracking-widest text-center disabled:opacity-50 text-slate-800"
            placeholder="••••••"/>
        </label>
      </div>

      {stage !== 'otp-sent' && (
        <button data-testid="admin-change-pin-send-otp"
          onClick={sendOtp} disabled={busy}
          className="w-full sm:w-auto text-[13px] font-bold px-3 py-2 rounded-full bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
          Send OTP to admin email
        </button>
      )}

      {stage === 'otp-sent' && (
        <div className="mt-2 pt-3 border-t border-slate-200">
          <p className="text-[13px] text-slate-600 mb-2 leading-relaxed">
            OTP sent to <span className="text-slate-800 font-mono">{emailHint}</span>.
            It expires in 10 minutes. Enter the 6-digit code:
          </p>
          <div className="flex gap-2 items-end">
            <label className="block flex-1">
              <span className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1">Email OTP</span>
              <input data-testid="admin-change-pin-otp"
                type="text" inputMode="numeric"
                value={otp} maxLength={6}
                onChange={(e) => setOtp(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 focus:border-amber-500 outline-none tracking-[0.4em] text-center text-slate-800"
                placeholder="••••••"/>
            </label>
            <button data-testid="admin-change-pin-verify"
              onClick={verifyAndSave} disabled={busy}
              className="text-[13px] font-bold px-3 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <ShieldCheck size={13}/>}
              Verify &amp; save
            </button>
          </div>
          <button data-testid="admin-change-pin-resend"
            type="button" onClick={sendOtp} disabled={busy}
            className="mt-2 text-[12px] text-slate-500 hover:text-slate-700 underline underline-offset-2">
            Resend OTP
          </button>
        </div>
      )}

      {err && (
        <div data-testid="admin-change-pin-error"
          className="mt-2 text-[13px] text-rose-600 font-bold">
          {err}
        </div>
      )}
      {flash && (
        <div data-testid="admin-change-pin-flash"
          className="mt-2 text-[13px] text-emerald-700 font-bold inline-flex items-center gap-1">
          <Check size={13}/> {flash}
        </div>
      )}
    </div>
  );
}
